import type { ActivityBoardItem, ActivityType, User } from 'library/types';

export type UserActivityRow = {
  activityTypeId: number;
  activityTypeTitle: string;
  spentHours: number;
  overtimeHours: number;
  totalHours: number;
};

export type UserSummaryRow = {
  id: number;
  nameSurname: string;
  spentHours: number;
  idleHours: number | null;
  overtimeHours: number;
  totalHours: number;
  activityRows: UserActivityRow[];
};

export type UserSummaryPermissionInput = {
  assignedUserId: number;
  activityTypeId: number;
  activityTypeTitle?: string;
  effectiveHours: number;
};

export type BuildUserSummaryRowsOptions = {
  activityBoardItems: readonly ActivityBoardItem[];
  effectivePermissions: readonly UserSummaryPermissionInput[];
  additionalPermissionActivityTypeIds?: readonly number[];
  activityTypes: readonly ActivityType[];
  scopedUsers: readonly User[];
  expectedHours: number;
  idleAvailable: boolean;
};

export type CalculateAdjustedExpectedHoursOptions = {
  effectivePermissions: readonly UserSummaryPermissionInput[];
  expectedHours: number;
  idleAvailable: boolean;
  selectedUserId: number | null;
};

type ActivityHours = {
  spentHours: number;
  overtimeHours: number;
};

type UserHours = ActivityHours & {
  permissionHours: number;
  activityTypeMap: Map<number, ActivityHours>;
};

const roundHours = (hours: number): number => Number(hours.toFixed(2));

const toNonNegativeHours = (hours: number): number => (Number.isFinite(hours) ? Math.max(0, hours) : 0);

const createActivityHours = (): ActivityHours => ({ spentHours: 0, overtimeHours: 0 });

const createUserHours = (): UserHours => ({
  spentHours: 0,
  overtimeHours: 0,
  permissionHours: 0,
  activityTypeMap: new Map(),
});

const getOrCreateActivityHours = (userHours: UserHours, activityTypeId: number): ActivityHours => {
  const existing = userHours.activityTypeMap.get(activityTypeId);
  if (existing) return existing;

  const activityHours = createActivityHours();
  userHours.activityTypeMap.set(activityTypeId, activityHours);

  return activityHours;
};

const buildPermissionActivityTypeIds = (
  activityTypes: readonly ActivityType[],
  effectivePermissions: readonly UserSummaryPermissionInput[],
  additionalPermissionActivityTypeIds: readonly number[]
): ReadonlySet<number> =>
  new Set([
    ...activityTypes.filter((activityType) => activityType.isPermissionActivity).map((activityType) => activityType.id),
    ...effectivePermissions.map((permission) => permission.activityTypeId),
    ...additionalPermissionActivityTypeIds,
  ]);

const buildActivityRows = (
  activityTypeMap: ReadonlyMap<number, ActivityHours>,
  activityTypeTitles: ReadonlyMap<number, string>
): UserActivityRow[] => {
  const rows: UserActivityRow[] = [];

  activityTypeMap.forEach((hours, activityTypeId) => {
    const spentHours = roundHours(hours.spentHours);
    const overtimeHours = roundHours(hours.overtimeHours);

    rows.push({
      activityTypeId,
      activityTypeTitle: activityTypeTitles.get(activityTypeId) ?? `Activity Type #${activityTypeId}`,
      spentHours,
      overtimeHours,
      totalHours: roundHours(spentHours + overtimeHours),
    });
  });

  return rows.sort((left, right) => right.totalHours - left.totalHours);
};

export const buildUserSummaryRows = ({
  activityBoardItems,
  effectivePermissions,
  additionalPermissionActivityTypeIds = [],
  activityTypes,
  scopedUsers,
  expectedHours,
  idleAvailable,
}: BuildUserSummaryRowsOptions): UserSummaryRow[] => {
  const usersById = new Map(scopedUsers.map((user) => [user.id, user]));
  const userHoursById = new Map(Array.from(usersById.keys(), (userId) => [userId, createUserHours()]));
  const activityTypeTitles = new Map(activityTypes.map((activityType) => [activityType.id, activityType.title]));
  effectivePermissions.forEach((permission) => {
    if (!activityTypeTitles.has(permission.activityTypeId) && permission.activityTypeTitle?.trim()) {
      activityTypeTitles.set(permission.activityTypeId, permission.activityTypeTitle.trim());
    }
  });
  const permissionActivityTypeIds = buildPermissionActivityTypeIds(
    activityTypes,
    effectivePermissions,
    additionalPermissionActivityTypeIds
  );

  activityBoardItems.forEach((item) => {
    const userHours = userHoursById.get(item.assignedUserId);
    if (!userHours || permissionActivityTypeIds.has(item.activityTypeId)) return;

    const hours = toNonNegativeHours(item.estimatedHours);
    const activityHours = getOrCreateActivityHours(userHours, item.activityTypeId);

    if (item.isOvertimeWork) {
      userHours.overtimeHours += hours;
      activityHours.overtimeHours += hours;
    } else {
      userHours.spentHours += hours;
      activityHours.spentHours += hours;
    }
  });

  effectivePermissions.forEach((permission) => {
    const userHours = userHoursById.get(permission.assignedUserId);
    if (!userHours) return;

    const effectiveHours = toNonNegativeHours(permission.effectiveHours);
    if (effectiveHours === 0) return;

    userHours.permissionHours += effectiveHours;
    // Permission rows remain visible in the detail panel, but they are not
    // regular work and therefore never increase the user's spent/total hours.
    getOrCreateActivityHours(userHours, permission.activityTypeId).spentHours += effectiveHours;
  });

  const normalizedExpectedHours = toNonNegativeHours(expectedHours);

  return Array.from(usersById.values())
    .map((user): UserSummaryRow => {
      const userHours = userHoursById.get(user.id) ?? createUserHours();
      const spentHours = roundHours(userHours.spentHours);
      const overtimeHours = roundHours(userHours.overtimeHours);
      const idleHours = idleAvailable
        ? roundHours(Math.max(0, normalizedExpectedHours - userHours.permissionHours - userHours.spentHours))
        : null;

      return {
        id: user.id,
        nameSurname: user.nameSurname,
        spentHours,
        idleHours,
        overtimeHours,
        totalHours: roundHours(spentHours + overtimeHours),
        activityRows: buildActivityRows(userHours.activityTypeMap, activityTypeTitles),
      };
    })
    .sort((left, right) => right.spentHours - left.spentHours);
};

export const calculateAdjustedExpectedHours = ({
  effectivePermissions,
  expectedHours,
  idleAvailable,
  selectedUserId,
}: CalculateAdjustedExpectedHoursOptions): number | null => {
  if (!idleAvailable) return null;

  const normalizedExpectedHours = toNonNegativeHours(expectedHours);
  if (selectedUserId === null) return roundHours(normalizedExpectedHours);

  const permissionHours = effectivePermissions.reduce(
    (total, permission) =>
      permission.assignedUserId === selectedUserId ? total + toNonNegativeHours(permission.effectiveHours) : total,
    0
  );

  return roundHours(Math.max(0, normalizedExpectedHours - permissionHours));
};
